import { useRef } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Dialog, DialogContent } from "@tenant/ui";
import styles from "./GridV2FaqAndTermsDialog.module.scss";
import type { GridV2FaqAndTermsTopic } from "./useGridV2FaqAndTermsDialog";

export interface GridV2FaqAndTermsDialogProps {
  /** Closed when null. */
  topic: GridV2FaqAndTermsTopic | null;
  /** X, Escape and the overlay. */
  onClose: () => void;
}

/**
 * Mobile sheet with the FAQ or the Terms and conditions, stacked over the
 * Grid V2 modal that opened it. Title, X and scroll come from the shared
 * Dialog; this only designs the body.
 */
export const GridV2FaqAndTermsDialog = ({
  topic,
  onClose,
}: GridV2FaqAndTermsDialogProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "GridV2FaqAndTermsDialog");

  // The topic goes null before the close animation ends; keep showing the
  // last one so the title does not flip to the FAQ while sliding out.
  const shownTopic = useRef<GridV2FaqAndTermsTopic>("faq");
  if (topic != null) {
    shownTopic.current = topic;
  }

  // Both resolved so the strings stay literal for the i18n lint.
  const copy = {
    faq: {
      title: t("gridV2.faqAndTerms.faqTitle"),
      content: t("gridV2.faqAndTerms.faqContent"),
    },
    terms: {
      title: t("gridV2.faqAndTerms.termsTitle"),
      content: t("gridV2.faqAndTerms.termsContent"),
    },
  }[shownTopic.current];

  return (
    <Dialog open={topic != null} onClose={onClose}>
      <DialogContent className={block} title={copy.title} showCloseButton>
        <p className={cn("text")}>{copy.content}</p>
      </DialogContent>
    </Dialog>
  );
};
